import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import RedoIcon from '@mui/icons-material/Redo';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { type BoardOperation } from './editor';
import type { LensDocument, LensState } from './types';
import type { BoardEditor } from './useBoardEdit';

type History = Pick<BoardEditor, 'undo' | 'redo' | 'canUndo' | 'canRedo' | 'status' | 'pending'>;

type EditBarProps = {
  history: History;
  preview: boolean;
  lensSettingsOpen: boolean;
  onMakeDefault?: () => void;
  onAddBlock: (anchor: HTMLElement) => void;
  onLensSettings: () => void;
  onPreview: (on: boolean) => void;
  onDone: () => void;
};

/** The operations that turn the period and filters on screen into the ones the Lens opens on. */
export function defaultViewOperations(document: LensDocument, state: LensState): { operations: Array<BoardOperation>; filters: Array<string> } {
  const changed = Object.entries(state.filters ?? {}).filter(([column, values]) => {
    const filter = document.filters.find((entry) => entry.column === column);
    return filter && values?.length && JSON.stringify(values) !== JSON.stringify(filter.value ?? []);
  });
  return {
    operations: [
      ...(state.period && state.period !== document.period.default ? [{ op: 'set' as const, id: 'lens', patch: { period: { default: state.period } } }] : []),
      ...changed.map(([column, values]) => ({ op: 'filter' as const, column, def: { value: values } })),
    ],
    filters: changed.map(([column]) => column),
  };
}

const STATUS_TEXT: Record<History['status'], string> = {
  idle: 'No changes yet',
  saving: 'Saving…',
  saved: 'Saved',
  unchanged: 'No changes to save',
  failed: 'Not saved',
};

export default function EditBar({ history, preview, lensSettingsOpen, onMakeDefault, onAddBlock, onLensSettings, onPreview, onDone }: EditBarProps) {
  if (preview)
    return (
      <div className="edit-bar preview" role="status">
        <VisibilityOutlinedIcon fontSize="small" />
        <span>Previewing as a reader. Nothing here is editable.</span>
        <button className="ebtn" onClick={() => onPreview(false)} type="button">
          Exit preview
        </button>
      </div>
    );
  const status = history.status === 'idle' && (history.canUndo || history.canRedo) ? 'saved' : history.status;
  return (
    <div aria-label="Edit bar" className="edit-bar" role="toolbar">
      <strong>Editing</strong>
      <span aria-hidden className="dot">
        ·
      </span>
      <span aria-live="polite" className="st">
        {STATUS_TEXT[status]}
      </span>
      <button className="ebtn" disabled={!history.canUndo} onClick={history.undo} title="Undo (Cmd+Z)" type="button">
        <UndoIcon fontSize="small" />
        Undo
      </button>
      <button className="ebtn" disabled={!history.canRedo} onClick={history.redo} title="Redo (Cmd+Shift+Z)" type="button">
        <RedoIcon fontSize="small" />
        Redo
      </button>
      <button aria-haspopup="menu" className="ebtn" onClick={(event) => onAddBlock(event.currentTarget)} type="button">
        <AddIcon fontSize="small" />
        Add block
      </button>
      <button aria-pressed={lensSettingsOpen} className="ebtn" onClick={onLensSettings} type="button">
        <TuneOutlinedIcon fontSize="small" />
        Lens settings
      </button>
      {onMakeDefault ? (
        <button className="chip-def" disabled={history.status === 'saving' || history.pending} onClick={onMakeDefault} type="button">
          Make this the default view
        </button>
      ) : null}
      <span className="sp" />
      <button className="ebtn" onClick={() => onPreview(true)} type="button">
        <VisibilityOutlinedIcon fontSize="small" />
        Preview as reader
      </button>
      <button className="ebtn done" onClick={onDone} type="button">
        Done
      </button>
    </div>
  );
}
